import { useState, useMemo, useCallback } from "react";
import { useHR } from "../context/HRContext";
import { useAuthStore } from "@/store/authStore";
import { DEFAULT_PAY_COMPONENTS } from "../constants/hrConstants";

export function usePaySetup() {
  const { employees, paySetups, savePaySetup } = useHR();
  const { companySettings } = useAuthStore();

  const [selectedEmpId, setSelectedEmpId] = useState(employees[0]?.id || "");
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [search, setSearch] = useState("");

  const [formData, setFormData] = useState({
    grossSalary: "",
    frequency: "monthly",
    pfNumber: "",
    esiNumber: "",
    isPayDisabled: false,
    bonus: "",
    employerPfRate: "",
    employerEsiRate: "",
    components: [],
  });
  const [formErrors, setFormErrors] = useState({});

  const filteredEmployees = useMemo(() => {
    const q = search.toLowerCase();
    return employees.filter(
      (e) => !q || e.name?.toLowerCase().includes(q) || e.id?.toLowerCase().includes(q)
    );
  }, [employees, search]);

  const getPaySetup = useCallback((empId) => {
    return paySetups.find((s) => s.employeeId === empId) || null;
  }, [paySetups]);

  const selectedSetup = useMemo(() => getPaySetup(selectedEmpId), [getPaySetup, selectedEmpId]);
  const selectedEmployee = useMemo(() => employees.find((e) => e.id === selectedEmpId), [employees, selectedEmpId]);

  const computedTotals = useMemo(() => {
    if (!formData.components?.length) return { earnings: 0, deductions: 0, net: 0, employerPf: 0, employerEsi: 0, ctc: 0 };
    const earnings = formData.components
      .filter((c) => c.type === "earning")
      .reduce((s, c) => s + Number(c.amount || 0), 0);
    const deductions = formData.components
      .filter((c) => c.type === "deduction")
      .reduce((s, c) => s + Number(c.amount || 0), 0);

    const pfRate = Number(formData.employerPfRate !== "" ? formData.employerPfRate : 13) / 100;
    const esiRate = Number(formData.employerEsiRate !== "" ? formData.employerEsiRate : 3.25) / 100;

    const employerPf = Math.round(earnings * pfRate); 
    const employerEsi = (earnings > 0 && earnings <= 21000) ? Math.round(earnings * esiRate) : 0;
    const bonus = Number(formData.bonus) || 0;

    const net = earnings - deductions;
    const ctc = (earnings * 12) + (employerPf * 12) + (employerEsi * 12) + bonus;

    return { earnings, deductions, net, employerPf, employerEsi, ctc };
  }, [formData.components, formData.bonus, formData.employerPfRate, formData.employerEsiRate]);

  const openForm = useCallback((empId) => {
    setSelectedEmpId(empId);
    const existing = paySetups.find((s) => s.employeeId === empId);
    if (existing) {
      setFormData({
        grossSalary: existing.grossSalary || "",
        frequency: existing.frequency || "monthly",
        pfNumber: existing.pfNumber || "",
        esiNumber: existing.esiNumber || "",
        isPayDisabled: existing.isPayDisabled || false,
        bonus: existing.bonus || "",
        employerPfRate: existing.employerPfRate !== undefined ? existing.employerPfRate : 13,
        employerEsiRate: existing.employerEsiRate !== undefined ? existing.employerEsiRate : 3.25,
        components: existing.components.map((c) => ({ ...c })),
      });
    } else {
      setFormData({
        grossSalary: "",
        frequency: "monthly",
        pfNumber: "",
        esiNumber: "",
        isPayDisabled: false,
        bonus: "",
        employerPfRate: 13,
        employerEsiRate: 3.25,
        components: DEFAULT_PAY_COMPONENTS.map((c) => ({ ...c, amount: 0 })),
      });
    }
    setFormErrors({});
    setIsFormOpen(true);
  }, [paySetups, companySettings]);

  const closeForm = useCallback(() => {
    setIsFormOpen(false);
    setFormErrors({});
  }, []);

  const updateGrossSalary = useCallback((val) => {
    setFormData((p) => {
      const gross = Number(val) || 0;
      let newComponents = p.components;

      if (gross >= 0) {
        const basic = Math.round(gross * 0.5);
        const hra = Math.round(basic * 0.4);
        const transport = gross > 0 ? 1600 : 0;
        const special = Math.max(0, gross - basic - hra - transport);

        const pf = Math.round(basic * 0.12);
        const esi = gross > 0 && gross <= 21000 ? Math.round(gross * 0.0075) : 0;
        const pt = gross > 0 ? 200 : 0;

        const calculatedAmounts = {
          "Basic Salary": basic,
          "HRA": hra,
          "Special Allowance": special,
          "Transport Allowance": transport,
          "PF (Employee)": pf,
          "ESI (Employee)": esi,
          "Professional Tax": pt,
          "TDS": 0,
        };

        newComponents = p.components.map((comp) => {
          if (calculatedAmounts[comp.name] !== undefined) {
            return { ...comp, amount: calculatedAmounts[comp.name] };
          }
          return comp;
        });
      }

      return { ...p, grossSalary: val, components: newComponents };
    });
  }, []);

  const updateComponent = useCallback((idx, field, value) => {
    setFormData((p) => ({
      ...p,
      components: p.components.map((c, i) =>
        i === idx ? { ...c, [field]: field === "amount" ? Number(value) : value } : c
      ),
    }));
  }, []);

  const addComponent = useCallback((type = "earning") => {
    setFormData((p) => ({
      ...p,
      components: [...p.components, { name: "", type, amount: 0 }],
    }));
  }, []);

  const removeComponent = useCallback((idx) => {
    setFormData((p) => ({
      ...p,
      components: p.components.filter((_, i) => i !== idx),
    }));
  }, []);

  const togglePayStatus = useCallback(async (employeeId) => {
    const setup = paySetups.find((s) => s.employeeId === employeeId);
    if (!setup) return;
    try {
      await savePaySetup(employeeId, { ...setup, isPayDisabled: !setup.isPayDisabled });
      return true;
    } catch (err) {
      console.error("Failed to toggle pay status", err);
      return false;
    }
  }, [paySetups, savePaySetup]);

  const validate = (data) => {
    const errs = {};
    if (!data.grossSalary || isNaN(Number(data.grossSalary)))
      errs.grossSalary = "Valid gross salary is required";
    const invalidComp = data.components.find((c) => !c.name?.trim());
    if (invalidComp) errs.components = "All components must have a name";
    return errs;
  };

  const save = useCallback(async () => {
    const errs = validate(formData);
    if (Object.keys(errs).length) { setFormErrors(errs); return false; }
    setIsSaving(true);
    try {
      await new Promise((r) => setTimeout(r, 350));
      const emp = employees.find((e) => e.id === selectedEmpId);
      savePaySetup(selectedEmpId, {
        grossSalary: Number(formData.grossSalary),
        frequency: formData.frequency,
        pfNumber: formData.pfNumber,
        esiNumber: formData.esiNumber,
        bonus: Number(formData.bonus) || 0,
        employerPfRate: Number(formData.employerPfRate !== "" ? formData.employerPfRate : 13),
        employerEsiRate: Number(formData.employerEsiRate !== "" ? formData.employerEsiRate : 3.25),
        components: formData.components,
        employeeName: emp?.name || "",
      });
      closeForm();
      return true;
    } finally {
      setIsSaving(false);
    }
  }, [formData, selectedEmpId, employees, savePaySetup, closeForm]);

  const updateField = useCallback((field, value) => {
    setFormData((p) => ({ ...p, [field]: value }));
  }, []);

  return {
    employees: filteredEmployees, allEmployees: employees,
    paySetups, selectedEmpId, setSelectedEmpId,
    selectedSetup, selectedEmployee,
    search, setSearch,
    isFormOpen, isSaving,
    formData, formErrors, computedTotals,
    openForm, closeForm,
    updateGrossSalary, updateField, updateComponent, addComponent, removeComponent, save,
    togglePayStatus,
    getPaySetup,
  };
}
